import { hideVisually } from 'polished';
import styled, { css } from 'styled-components';
import { Flex } from '@bigcommerce/big-design';
import {
	theme as defaultTheme,
	breakpointsOrder,
} from '@bigcommerce/big-design-theme';

const getResponsiveDisplay = (displayProp, theme, cssKey) => {
	const breakpointKeys = Object.keys(displayProp).sort(
		(firstBreakpoint, secondBreakpoint) =>
			breakpointsOrder.indexOf(firstBreakpoint) -
			breakpointsOrder.indexOf(secondBreakpoint),
	);

	return breakpointKeys.map(
		(breakpointKey) =>
			css`
				${theme.breakpoints[breakpointKey]} {
					${getSimpleDisplay(displayProp[breakpointKey], cssKey)}
				}
			`,
	);
};

const getDisplayStyles = (displayProp, theme, cssKey) => {
	if (typeof displayProp === 'object')
		return getResponsiveDisplay(displayProp, theme, cssKey);

	if (typeof displayProp === 'string' || typeof displayProp === 'number')
		return getSimpleDisplay(displayProp, cssKey);

	return [];
};

const getSimpleDisplay = (displayProp, cssKey) => css`
	${cssKey}: ${displayProp}
`;

const Timings = {
	short: '150ms',
};

const Easings = {
	hover: 'ease-out',
};

const withTableColumnDisplay = () => css`
	${({ display, theme }) =>
		display && getDisplayStyles(display, theme, 'display')};
`;

const StyledTableHead = styled.thead`
	${({ hidden }) => hidden && hideVisually()}
`;

const StyledTableHeaderCell = styled.th`
	${withTableColumnDisplay()}
	background-color: ${({ theme }) => theme.colors.secondary10};
	border-bottom: ${({ theme }) => theme.border.box};
	border-top: ${({ theme }) => theme.border.box};
	box-sizing: border-box;
	color: ${({ theme }) => theme.colors.secondary60};
	font-size: ${({ theme }) => theme.typography.fontSize.medium};
	padding: ${({ theme }) => theme.spacing.small};
	white-space: nowrap;

	&:first-of-type {
		padding-left: ${({ theme }) => theme.spacing.xLarge};
	}

	&:last-of-type {
		padding-right: ${({ theme }) => theme.spacing.xLarge};
	}

	${({ isSortable }) =>
		isSortable &&
		css`
			cursor: pointer;
		`};
	${({ width }) =>
		width !== undefined &&
		css`
			width: ${typeof width === 'string' ? width : `${width}px`};
		`};
	${({ theme, stickyHeader, stickyHeight }) =>
		stickyHeader &&
		stickyHeight >= 0 &&
		css`
			${theme.breakpoints.tablet} {
				position: sticky;
				top: ${theme.helpers.remCalc(stickyHeight)};
				z-index: ${theme.zIndex.sticky};
			}
		`}
`;

const StyledTableHeaderCheckbox = styled(StyledTableHeaderCell)`
	width: ${({ theme }) =>
		theme.helpers.addValues(theme.spacing.xLarge, theme.spacing.small)};
	white-space: nowrap;
`;

const StyledFlex = styled(Flex)`
	${({ align }) => {
		switch (align) {
			case 'center':
				return css`
					justify-content: center;
				`;
			case 'right':
				return css`
					justify-content: flex-end;
				`;
			default:
				return css`
					justify-content: flex-start;
				`;
		}
	}};
	${({ hide }) => hide && hideVisually()};
`;

const withTransition = (
	properties,
	duration = Timings.short,
	ease = Easings.hover,
) => css`
	transition: all ${duration} ${ease};
	transition-property: ${properties.join(', ')};
`;

const StyledTableRow = styled.tr`
	${withTransition(['background-color'])}
	display: ${({ isDragging }) => (isDragging ? 'table' : 'table-row')};
	background-color: ${({ isSelected, theme }) =>
		isSelected ? theme.colors.primary10 : 'transparent'};

	&:hover {
		background-color: ${({ theme }) => theme.colors.secondary20};
	}
`;

const StyledTable = styled.table`
	border-color: transparent;
	border-spacing: 0;

	color: ${({ theme }) => theme.colors.secondary70};
	text-align: left;
	width: 100%;
`;

const StyledTableDataCell = styled.td`
	${withTableColumnDisplay()}
	background-color: ${({ theme }) => theme.colors.white};
	box-sizing: border-box;
	color: ${({ theme }) => theme.colors.secondary70};
	font-size: ${({ theme }) => theme.typography.fontSize.medium};
	padding: ${({ theme, withPadding }) =>
		withPadding ? theme.spacing.small : 0};

	&:first-of-type {
		padding-left: ${({ theme, withPadding }) =>
			withPadding ? theme.spacing.xLarge : 0};
	}

	&:last-of-type {
		padding-right: ${({ theme, withPadding }) =>
			withPadding ? theme.spacing.xLarge : 0};
	}

	${({ theme, withBorder }) =>
		withBorder &&
		css`
			border-bottom: ${theme.border.box};
		`}
	${({ align }) =>
		align &&
		css`
			text-align: ${align};
		`};
	${({ verticalAlign }) =>
		verticalAlign &&
		css`
			vertical-align: ${verticalAlign};
		`};
	${({ width }) =>
		width !== undefined &&
		css`
			width: ${typeof width === 'string' ? width : `${width}px`};
		`};
`;

const StyledTableBody = styled.tbody`
	${({ theme, withFirstRowBorder }) =>
		withFirstRowBorder &&
		css`
			tr:first-of-type > td {
				border-top: ${theme.border.box};
			}
		`}
`;

StyledTableBody.defaultProps = { theme: defaultTheme };
StyledTableDataCell.defaultProps = { theme: defaultTheme };
StyledTable.defaultProps = { theme: defaultTheme };
StyledTableRow.defaultProps = { theme: defaultTheme };
StyledTableHead.defaultProps = { theme: defaultTheme };
StyledFlex.defaultProps = { theme: defaultTheme };
StyledTableHeaderCell.defaultProps = { theme: defaultTheme };
StyledTableHeaderCheckbox.defaultProps = { theme: defaultTheme };

export {
	withTableColumnDisplay,
	StyledTableHead,
	StyledTableHeaderCell,
	StyledTableHeaderCheckbox,
	StyledFlex,
	withTransition,
	StyledTableRow,
	StyledTable,
	StyledTableDataCell,
	StyledTableBody,
};
